import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { mockCourses, type Course } from '@/mocks/courses';
import { mapDbCourse, formatPrice } from '@/lib/courses';
import { useAuth } from '@/context/AuthContext';

function LearnDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('courses')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (err) throw err;
      if (data) {
        setCourse(mapDbCourse(data as never));
      } else {
        setCourse(mockCourses.find((c) => c.id === id) || null);
      }
    } catch {
      setCourse(mockCourses.find((c) => c.id === id) || null);
    } finally {
      setLoading(false);
    }
  };

  const checkEnrollment = async () => {
    if (!user || !id) return;
    const { data } = await supabase
      .from('enrollments')
      .select('id')
      .eq('course_id', id)
      .eq('user_id', user.id)
      .maybeSingle();
    setAlreadyEnrolled(!!data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    checkEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  const handleEnroll = async () => {
    if (!user) {
      navigate('/auth/login', { state: { from: { pathname: `/learn/${id}` } } });
      return;
    }
    if (!course) return;
    setEnrolling(true);
    setFormError('');
    const { error: err } = await supabase.from('enrollments').insert({
      course_id: course.id,
      user_id: user.id,
    });
    setEnrolling(false);
    if (err) {
      setFormError(err.message);
      return;
    }
    setAlreadyEnrolled(true);
    setSuccess(true);
  };

  const objectives = course?.objectives ? course.objectives.split('\n').filter(Boolean) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
            <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/10">
              <i className="ri-error-warning-line text-3xl text-primary-400" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">
              Formation introuvable
            </h1>
            <p className="text-sm text-foreground-400 mb-6">
              Cette formation n&apos;existe plus ou a été retirée.
            </p>
            <Link
              to="/learn"
              className="inline-block px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
            >
              Voir toutes les formations
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 md:pt-32 pb-16">
          {/* Header */}
          <div className="mb-6">
            <nav aria-label="Breadcrumb" className="text-xs text-foreground-400 mb-4 flex items-center gap-2 flex-wrap">
              <Link to="/learn" className="hover:text-primary-400 transition-colors">Formations</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-500">{course.category}</span>
            </nav>

            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
              <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 leading-tight">
                  {course.title}
                </h1>
                <p className="text-foreground-400 mt-1">
                  Par {course.instructor}
                </p>
                <div className="flex items-center gap-2 flex-wrap mt-3">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-400 whitespace-nowrap">
                    {course.category}
                  </span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-background-200/70 text-foreground-500 whitespace-nowrap">
                    {course.level}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-translate-2" />
                    {course.language}
                  </span>
                  {course.durationHours !== null && (
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-time-line" />
                      {course.durationHours}h
                    </span>
                  )}
                  {course.lessonsCount !== null && (
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-play-list-2-line" />
                      {course.lessonsCount} leçons
                    </span>
                  )}
                </div>
              </div>
              <div className="text-left md:text-right shrink-0">
                <p className="font-heading font-bold text-2xl text-primary-400 whitespace-nowrap">
                  {formatPrice(course.price)}
                </p>
              </div>
            </div>
          </div>

          {/* Success banner */}
          {success && (
            <div className="p-4 rounded-xl bg-accent-500/10 border border-accent-400/30 text-sm text-accent-400 mb-6 flex items-center gap-3">
              <i className="ri-checkbox-circle-line text-xl" />
              Vous êtes inscrit(e) à cette formation ! Le contenu complet (vidéos et tutoriels) est maintenant disponible.
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-6">{error}</div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-6">
              <div className="relative h-56 md:h-72 rounded-xl overflow-hidden">
                <img
                  src={course.cover}
                  alt={`Formation ${course.title}`}
                  title={`${course.title} - ${course.category}`}
                  className="w-full h-full object-cover"
                />
              </div>

              <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
                    <i className="ri-file-text-line text-primary-400" />
                  </span>
                  À propos de cette formation
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed whitespace-pre-line">
                  {course.description}
                </p>
              </section>

              {objectives.length > 0 && (
                <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
                      <i className="ri-check-double-line text-primary-400" />
                    </span>
                    Ce que vous apprendrez
                  </h2>
                  <ul className="space-y-2.5">
                    {objectives.map((obj, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground-400">
                        <i className="ri-checkbox-circle-line text-primary-400 mt-0.5" />
                        {obj}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 lg:sticky lg:top-24">
                <h3 className="font-heading font-semibold text-foreground-50 mb-4">
                  Accéder à la formation
                </h3>

                {formError && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">
                    {formError}
                  </div>
                )}

                {alreadyEnrolled ? (
                  <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-400/30 text-center">
                    <i className="ri-checkbox-circle-line text-2xl text-accent-400" />
                    <p className="text-sm font-semibold text-foreground-50 mt-2">Vous êtes inscrit(e)</p>
                    <p className="text-xs text-foreground-400 mt-1">
                      Contenu complet disponible dans votre espace.
                    </p>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleEnroll}
                      disabled={enrolling}
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap mb-3 disabled:opacity-60"
                    >
                      <i className="ri-shopping-cart-line" />
                      {enrolling ? 'Inscription...' : course.price !== null && course.price > 0
                        ? `Acheter · ${formatPrice(course.price)}`
                        : "S'inscrire gratuitement"}
                    </button>
                    {!user && (
                      <p className="text-xs text-foreground-400 text-center">
                        Connectez-vous pour vous inscrire à cette formation.
                      </p>
                    )}
                  </>
                )}

                <div className="mt-5 pt-5 border-t border-background-200/70 space-y-2 text-sm">
                  {course.durationHours !== null && (
                    <div className="flex items-center gap-2 text-foreground-400">
                      <i className="ri-time-line text-primary-400" />
                      {course.durationHours} heures de contenu
                    </div>
                  )}
                  {course.lessonsCount !== null && (
                    <div className="flex items-center gap-2 text-foreground-400">
                      <i className="ri-play-list-2-line text-primary-400" />
                      {course.lessonsCount} leçons vidéo
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-foreground-400">
                    <i className="ri-infinity-line text-primary-400" />
                    Accès à vie
                  </div>
                  <div className="flex items-center gap-2 text-foreground-400">
                    <i className="ri-device-line text-primary-400" />
                    Mobile &amp; ordinateur
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default LearnDetail;